import {
  ensureTopicPartitionIsLoadedWithTopicDetails,
  fillPartitionedSubTopics,
  peekNthMessage,
  topicFullNameToDetail,
  fetchTopicStats,
  PARTITION_SEPARATOR,
  type TopicActionParameters,
  type TopicDetails,
  type TopicMap
} from '@/api/topics'
import {
  getAllTopics,
  createTopic as apiCreateTopic,
  updateTopic as apiUpdateTopic,
  deleteTopic as apiDeleteTopic,
  createSubscription as apiCreateSubscription,
  deleteSubscription as apiDeleteSubscription,
  resetCursor,
  skipAllMessages,
  getTopicBundle,
  getTopicBroker as apiGetTopicBroker,
  fetchAllSchemas,
  deleteSchema,
  postSchema,
  getInternalStats,
  unloadTopic as apiUnloadTopic,
  compactionStatus as apiCompactionStatus,
  compact,
  getPermissionsOnTopic,
  grantPermissionsOnTopic,
  revokePermissionsOnTopic,
  getPartitionedMetadata,
  getBacklogQuota,
  setBacklogQuota as setBacklogQuotaOnTopic,
  getDispatchRate,
  setDispatchRate as setDispatchRateOnTopic,
  getSubscriptionDispatchRate,
  setSubscriptionDispatchRate as setSubscriptionDispatchRateOnTopic,
  getMessageTTL,
  setMessageTTL as setMessageTTLOnTopic,
  getRetention,
  setRetention as setRetentionOnTopic,
  getCompactionThreshold,
  setCompactionThreshold as setCompactionThresholdOnTopic,
  getMaxConsumers,
  setMaxConsumers as setMaxConsumersOnTopic,
  getMaxConsumersPerSubscription,
  setMaxConsumersPerSubscription as setMaxConsumersPerSubscriptionOnTopic,
  getMaxProducers,
  setMaxProducers as setMaxProducersOnTopic,
  getSubscribeRate,
  setSubscribeRate as setSubscribeRateOnTopic,
  getTopicReplication,
  setReplicationClusters as setReplicationClustersOnTopic
} from '@/api/topics'
import { i18n } from '@/lang'
import {
  LongRunningProcessStatusStatusEnum,
  type GetSchemaResponse,
  type GetSchemaResponseTypeEnum,
  type InlineResponse200,
  type NonPersistentTopicStats,
  type PartitionedTopicStatsImpl,
  type PersistentTopicInternalStats,
  type PartitionedTopicInternalStats,
  type TopicStats,
  type Policies,
  type RetentionPolicies,
  type DispatchRateImpl,
  type SubscribeRate,
  type BacklogQuotaImpl
} from '@streamnative/pulsar-admin-client-typescript'
import type { PulsarState } from './usePulsarState'
import axios from 'axios'
import { useTenantNamespace } from './useTenantNamespace'
import { isEmpty, difference, merge } from 'lodash-es'
import { getTopicBacklogCountBySize } from '@/utils/topicSubscriptionFilters'

type TopicBroker = InlineResponse200 & { brokerUrlTls?: string }

export type Message = {
  properties: string
  messageId: string
  message: string
  ledgerId: string
  entryId: string
  data: string
}

let lastSeen: string | undefined = undefined
const { t } = i18n.global
const topicMap = ref<TopicMap>({})
const topicStats = ref<PartitionedTopicStatsImpl | TopicStats | NonPersistentTopicStats>({})
const bundleRange = ref('')
const topicBroker = ref<TopicBroker>({
  brokerUrl: '',
  httpUrl: '',
  nativeUrl: '',
  brokerUrlSsl: '',
  brokerUrlTls: ''
})
const schemas = ref<Array<GetSchemaResponse>>([])
const topicInternalStats = ref<PersistentTopicInternalStats>({})
const partitionedTopicnternalStats = ref<PartitionedTopicInternalStats>({})
const compactionStatus = ref<LongRunningProcessStatusStatusEnum>(
  LongRunningProcessStatusStatusEnum.NOTRUN
)
const messages = ref<Message[]>([])
const errorGettingTopics = ref<{ message: string; e: unknown } | undefined>(undefined)
const isTopicLoading = ref(true)
const activeTopicPolicies = ref<Policies>({})

const resetTopicState = () => {
  topicMap.value = {}
  topicStats.value = {}
  bundleRange.value = ''
  topicBroker.value = {
    brokerUrl: '',
    httpUrl: '',
    nativeUrl: '',
    brokerUrlSsl: '',
    brokerUrlTls: ''
  }
  schemas.value = []
  topicInternalStats.value = {}
  partitionedTopicnternalStats.value = {}
  compactionStatus.value = LongRunningProcessStatusStatusEnum.NOTRUN
  messages.value = []
}

/**
 * Given a list of topic names, fetch partition count if their partition count is undefined.
 * This will allow us lazily fetch partition counts if we don't know.
 * @param topicNames
 */
const ensureTopicPartitionIsLoaded = async (topicNames: string[]) => {
  const _topicMap = await ensureTopicPartitionIsLoadedWithTopicDetails(
    topicNames.map(topicName => topicMap.value[topicName])
  )
  Object.assign(topicMap.value, _topicMap)
}

const ensureTopicMetricsIsLoaded = async (topicNames: string[]) => {
  await Promise.all(
    topicNames.map(async tn => {
      const params = apiPerTopicParamsWithTopicMap(tn, topicMap.value)
      try {
        const metrics = (await fetchTopicStats(params)).data
        if (topicMap.value[tn]) {
          const backlogCount = getTopicBacklogCountBySize(
            metrics.subscriptions ?? {},
            metrics.backlogSize
          )
          topicMap.value[tn].metrics = {
            storageSize: metrics.storageSize || 0,
            backlogSize: metrics.backlogSize || 0,
            backlogCount
          }
        }
      } catch (error) {
        console.error(error)
        if (topicMap.value[tn]) {
          topicMap.value[tn].metrics = {
            storageSize: undefined,
            backlogSize: undefined,
            backlogCount: undefined
          }
        }
        throw error
      }
    })
  )
}

const getTopics = async (params?: {
  organization?: string
  clusterUid?: string
  tenant?: string
  namespace?: string
}) => {
  isTopicLoading.value = true
  const { mustOrganization, mustClusterUid, mustTenant, mustNamespace } = usePulsarState()
  let topics = {}
  try {
    topics = await getAllTopics({
      organization: params?.organization || mustOrganization(),
      clusterUid: params?.clusterUid || mustClusterUid(),
      tenant: params?.tenant || mustTenant(),
      namespace: params?.namespace || mustNamespace()
    })
    errorGettingTopics.value = undefined
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    errorGettingTopics.value = {
      e,
      message: getErrorMessage(e, 'Unknown error')
    }
    throw e
  } finally {
    isTopicLoading.value = false
  }

  // for noPersPart, we can infer partition details from noPersNoPart if there are data for it.
  // if noPersNoPart is missing partition info, partition info will be laziy fetch via `ensureTopicPartitionIsLoaded`.
  // Object.values(noPersPart).forEach(detail => {
  //   if (detail.partitionId) {
  //     // increment partition count as persNoPart includes individual partitioned topic names
  //     const currentPartitionCount = noPersNoPart[detail.topicRootName].partitionCount
  //     noPersNoPart[detail.topicRootName].partitionCount = (currentPartitionCount ?? 0) + 1
  //   }
  // })
  topicMap.value = topics
}

export const getTopicPolicies = async () => {
  const params = apiPerTopicParams()

  const { activeCluster } = useCluster()
  const { setNamespacePolicies, tenantNamespaces } = useTenantNamespace()
  await setNamespacePolicies(params)

  const cName = activeCluster.value?.metadata.name ?? ''
  const activeNamespacePolicies = tenantNamespaces.value[params.tenant]?.[params.namespace] ?? {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const policies: any = {}
  const actions = [
    (async () => {
      const { data: backlog_quota_map } = await getBacklogQuota(params)
      if (!isEmpty(backlog_quota_map)) {
        policies.backlog_quota_map = backlog_quota_map
      }
    })(),
    (async () => {
      const { data: auth_policies } = await getPermissionsOnTopic(params)
      if (!isEmpty(auth_policies)) {
        policies.auth_policies = auth_policies
      }
    })(),
    (async () => {
      const { data: topicDispatchRate } = await getDispatchRate(params)
      if (!isEmpty(topicDispatchRate)) {
        policies.topicDispatchRate = {
          [cName]: topicDispatchRate
        }
      }
    })(),
    (async () => {
      const { data: message_ttl_in_seconds } = await getMessageTTL(params)
      if (message_ttl_in_seconds !== undefined && message_ttl_in_seconds + '' !== '') {
        policies.message_ttl_in_seconds = message_ttl_in_seconds
      }
    })(),
    (async () => {
      const { data: retention_policies } = await getRetention(params)
      if (!isEmpty(retention_policies)) {
        policies.retention_policies = retention_policies
      }
    })(),
    (async () => {
      const { data: compaction_threshold } = await getCompactionThreshold(params)
      if (compaction_threshold !== undefined && compaction_threshold + '' !== '') {
        policies.compaction_threshold = compaction_threshold
      }
    })(),
    (async () => {
      const { data: subscriptionDispatchRate } = await getSubscriptionDispatchRate(params)
      if (!isEmpty(subscriptionDispatchRate)) {
        policies.subscriptionDispatchRate = {
          [cName]: subscriptionDispatchRate
        }
      }
    })(),
    (async () => {
      const { data: clusterSubscribeRate } = await getSubscribeRate(params)
      if (!isEmpty(clusterSubscribeRate)) {
        policies.clusterSubscribeRate = {
          [cName]: clusterSubscribeRate
        }
      }
    })(),
    (async () => {
      const { data: max_producers_per_topic } = await getMaxProducers(params)
      if (max_producers_per_topic !== undefined && max_producers_per_topic + '' !== '') {
        policies.max_producers_per_topic = max_producers_per_topic
      }
    })(),
    (async () => {
      const { data: max_consumers_per_topic } = await getMaxConsumers(params)
      if (max_consumers_per_topic !== undefined && max_consumers_per_topic + '' !== '') {
        policies.max_consumers_per_topic = max_consumers_per_topic
      }
    })(),
    (async () => {
      const { data: replication_clusters } = await getTopicReplication({
        ...params,
        applied: true
      })
      if (replication_clusters !== undefined) {
        policies.replication_clusters = replication_clusters
      }
    })(),
    (async () => {
      const { data: max_consumers_per_subscription } = await getMaxConsumersPerSubscription(params)
      if (
        max_consumers_per_subscription !== undefined &&
        max_consumers_per_subscription + '' !== ''
      ) {
        policies.max_consumers_per_subscription = max_consumers_per_subscription
      }
    })()
  ]
  try {
    await Promise.all(actions)
  } catch (error) {
    useError(error)
  }

  // The topic policies inherit from namespace by default, so deep merge namespace policies and topic policies
  const _policies = merge({}, activeNamespacePolicies, policies)

  // since merge() will merge arrays by index, we need to explicitly override replication_clusters
  // because policies.replication_clusters (from getTopicReplication with applied=true) contains the effective list
  // and we don't want to merge it with namespace policies
  if (policies.replication_clusters) {
    _policies.replication_clusters = policies.replication_clusters
  }

  for (const [key] of Object.entries(_policies.auth_policies)) {
    if (key === 'namespace_auth') {
      for (const [nkey] of Object.entries(_policies.auth_policies.namespace_auth)) {
        delete _policies.auth_policies[nkey]
      }
    } else if (key === 'destination_auth' || key === 'subscription_auth_roles') {
      delete _policies.auth_policies[key]
    }
  }

  activeTopicPolicies.value = _policies

  return _policies
}

const apiBaseParams = (
  params:
    | {
        organization?: string
        clusterUid?: string
        tenant?: string
        namespace?: string
      }
    | undefined
) => {
  const { mustOrganization, mustClusterUid, mustTenant, mustNamespace } = usePulsarState()
  return {
    organization: params?.organization || mustOrganization(),
    clusterUid: params?.clusterUid || mustClusterUid(),
    tenant: params?.tenant || mustTenant(),
    namespace: params?.namespace || mustNamespace()
  }
}

/**
 * This function will create params with the global topic map
 */
const apiPerTopicParams = (shortTopicName?: string) => {
  const { mustTopic } = usePulsarState()
  return apiPerTopicParamsWithTopicMap(shortTopicName ?? mustTopic(), topicMap.value)
}

/**
 * This function will create params with a specify topic map
 */
const apiPerTopicParamsWithTopicMap = (
  shortTopicName: string,
  topicMap: TopicMap
): TopicActionParameters => {
  const { mustOrganization, mustClusterUid, mustTenant, mustNamespace } = usePulsarState()
  const topicDetail = topicMap[shortTopicName]
  if (!topicDetail) {
    throw Error(`${shortTopicName} cannot be found`)
  }
  return {
    topicName: topicDetail.topicName,
    partitioned: topicDetail.partitioned,
    organization: topicDetail.organization ?? mustOrganization(),
    clusterUid: topicDetail.clusterUid ?? mustClusterUid(),
    tenant: topicDetail.tenant ?? mustTenant(),
    namespace: topicDetail.namespace ?? mustNamespace(),
    persistency: topicDetail.persistency
  }
}

const createTopic = async (topicDetail: TopicDetails) => {
  if (!topicDetail.topicName) {
    throw Error(t('topic.topicPlaceholder'))
  }
  if (topicDetail.partitionCount === undefined || topicDetail.partitionCount < 0) {
    throw Error(t('topic.partitionPlaceholder'))
  }
  const params = apiBaseParams({
    organization: topicDetail.organization,
    clusterUid: topicDetail.clusterUid,
    tenant: topicDetail.tenant,
    namespace: topicDetail.namespace
  })
  try {
    await apiCreateTopic({
      ...params,
      persistency: topicDetail.persistency,
      partitioned: (topicDetail.partitionCount as number) > 0,
      topicName: topicDetail.topicName,
      partitionCount: topicDetail.partitionCount as number
    })
    // add newly created topic to the topic map
    topicMap.value[topicDetail.topicName] = topicDetail
    fillPartitionedSubTopics(topicDetail, topicMap.value)
  } catch (e) {
    throw Error(getErrorMessage(e, t('topic.notification.createTopicFailed')))
  }
}

const updateTopic = async (topicDetail: TopicDetails) => {
  if (!topicDetail.topicName) {
    throw Error(t('topic.topicPlaceholder'))
  }
  if (topicDetail.partitionCount === undefined || topicDetail.partitionCount < 0) {
    throw Error(t('topic.partitionPlaceholder'))
  }
  const params = apiBaseParams({
    organization: topicDetail.organization,
    clusterUid: topicDetail.clusterUid,
    tenant: topicDetail.tenant,
    namespace: topicDetail.namespace
  })
  try {
    await apiUpdateTopic({
      ...params,
      persistency: topicDetail.persistency,
      partitioned: (topicDetail.partitionCount as number) > 0,
      topicName: topicDetail.topicName,
      partitionCount: topicDetail.partitionCount as number
    })
    // add newly created topic to the topic map
    topicMap.value[topicDetail.topicName] = topicDetail
    fillPartitionedSubTopics(topicDetail, topicMap.value)
  } catch (e) {
    throw Error(getErrorMessage(e, t('topic.notification.updateTopicFailed')))
  }
}

const deleteTopic = async (shortTopicName: string, isForce = false) => {
  await ensureTopicPartitionIsLoaded([shortTopicName])
  const params = apiPerTopicParams(shortTopicName)
  await apiDeleteTopic(params, isForce)
  delete topicMap.value[params.topicName]
}

/**
 * This function will not impact the global topic stats, it just return the topic stats data from the response
 */
const getTopicStats = async (shortTopicName: string) => {
  const stats = await getIndividualTopicStats(shortTopicName)
  if (stats) {
    topicStats.value = stats
  }
}

const getIndividualTopicStats = async (shortTopicName: string) => {
  Object.assign(
    topicMap,
    await ensureTopicPartitionIsLoadedWithTopicDetails([topicMap.value[shortTopicName]])
  )
  const params = apiPerTopicParamsWithTopicMap(shortTopicName, topicMap.value)
  try {
    const stats = (await fetchTopicStats(params, true)).data
    if (topicMap.value[shortTopicName]) {
      topicMap.value[shortTopicName].metrics = stats
    }
    return stats
  } catch (e) {
    throw Error(
      getErrorMessage(
        e,
        t(
          `topic.notification.${
            params.partitioned ? 'getPartitionedTopicStats' : 'getTopicsStatsFailed'
          }`
        )
      )
    )
  }
}

const createSubscription = async (shortTopicName: string, subscriptionName: string) => {
  if (!subscriptionName) {
    throw Error(t('topic.subscription.subNotification'))
  }
  Object.assign(
    topicMap,
    await ensureTopicPartitionIsLoadedWithTopicDetails([topicMap.value[shortTopicName]])
  )
  const params = apiPerTopicParamsWithTopicMap(shortTopicName, topicMap.value)

  try {
    await apiCreateSubscription({
      ...params,
      subscriptionName: encodeURIComponent(subscriptionName)
    })
  } catch (e) {
    throw Error(getErrorMessage(e, t('topic.subscription.failedToCreate')))
  }

  await getTopicStats(shortTopicName)
}

const deleteSubscription = async (shortTopicName: string, subscriptionName: string) => {
  await apiDeleteSubscription({
    ...apiPerTopicParams(shortTopicName),
    subscriptionName: encodeURIComponent(subscriptionName)
  })
  const topicSubs = topicStats.value.subscriptions ?? {}
  delete topicSubs[subscriptionName]
  topicStats.value = { ...topicStats.value, subscriptions: topicSubs }
}

const resetAllSubMessage = async (
  shortTopicName: string,
  subscriptionName: string,
  minutes: number
) => {
  const timestamp = Math.floor(new Date().getTime()) - minutes * 60000
  try {
    await resetCursor({
      ...apiPerTopicParams(shortTopicName),
      subscriptionName: encodeURIComponent(subscriptionName),
      timestamp
    })
  } catch (e) {
    throw Error(getErrorMessage(e, t('topic.subscription.failedToReset')))
  }
}

const clearAllSubMessage = async (shortTopicName: string, subscriptionName: string) => {
  try {
    await skipAllMessages({
      ...apiPerTopicParams(shortTopicName),
      subscriptionName: encodeURIComponent(subscriptionName)
    })
  } catch (e) {
    throw Error(getErrorMessage(e))
  }
}

const getBundleRange = async (shortTopicName: string) => {
  try {
    const _bundleRange = await getTopicBundle(apiPerTopicParams(shortTopicName))
    bundleRange.value = _bundleRange.data
  } catch (e) {
    throw Error(getErrorMessage(e, 'getBundleRange Error'))
  }
}

const getTopicBroker = async (shortTopicName: string) => {
  try {
    const _topicBroker = await apiGetTopicBroker(apiPerTopicParams(shortTopicName))
    topicBroker.value = _topicBroker.data
  } catch (e) {
    throw Error(getErrorMessage(e, 'getAllSchemas Error'))
  }
}

const getAllSchemas = async (shortTopicName: string) => {
  try {
    const _schemas = await fetchAllSchemas(apiPerTopicParams(shortTopicName))
    schemas.value = _schemas.data.getSchemaResponses ?? []
  } catch (e) {
    throw Error(getErrorMessage(e, 'getAllSchemas Error'))
  }
}

const deleteAllSchemas = async (shortTopicName: string) => {
  try {
    await deleteSchema(apiPerTopicParams(shortTopicName))
    schemas.value = []
  } catch (e) {
    throw Error(getErrorMessage(e, t('schema.deleteSchemaFailed')))
  }
}

const createSchema = async (
  shortTopicName: string,
  type: GetSchemaResponseTypeEnum,
  data: string,
  propertyEntries: [string, string][]
) => {
  const keys = new Set()
  propertyEntries.forEach(([key, value]) => {
    if (!key || !value) {
      throw Error('A key and a value must be defined for all properties')
    }
    keys.add(key)
  })

  if (keys.size !== propertyEntries.length) {
    throw Error(t('schema.keyIsExistNotification'))
  }

  const properties = Object.fromEntries(propertyEntries)
  const res = await postSchema({
    ...apiPerTopicParams(shortTopicName),
    body: {
      type,
      schema: data,
      properties
    }
  })

  const newSchema: GetSchemaResponse = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    version: res.data?.version?.version,
    type,
    data,
    properties
  }
  const newSchemas = schemas.value
  newSchemas.push(newSchema)
  schemas.value = newSchemas
}

const getTopicStatsInternal = async (shortTopicName: string) => {
  const params = apiPerTopicParams(shortTopicName)
  const { data } = await getInternalStats(params)
  if (params.partitioned) {
    partitionedTopicnternalStats.value = data as PartitionedTopicInternalStats
    topicInternalStats.value = {}
    if (topicMap.value[shortTopicName]) {
      topicMap.value[shortTopicName].internalMetrics = partitionedTopicnternalStats.value
    }
  } else {
    partitionedTopicnternalStats.value = {}
    topicInternalStats.value = data as PersistentTopicInternalStats
    topicMap.value[shortTopicName].internalMetrics = topicInternalStats.value
  }
}

const unloadTopic = async (shortTopicName: string) => {
  await apiUnloadTopic(apiPerTopicParams(shortTopicName))
}

const getCompactionStatus = async (shortTopicName: string) => {
  try {
    const { data } = await apiCompactionStatus(apiPerTopicParams(shortTopicName))
    compactionStatus.value = data.status ?? LongRunningProcessStatusStatusEnum.NOTRUN
  } catch (e) {
    throw Error(getErrorMessage(e, t('topic.notification.compactionFailed')))
  }
}

const compactTopic = async (shortTopicName: string) => {
  await compact(apiPerTopicParams(shortTopicName))
  await getCompactionStatus(shortTopicName)
}

const getPermissions = async (shortTopicName: string) => {
  try {
    return await getPermissionsOnTopic(apiPerTopicParams(shortTopicName))
  } catch (e) {
    throw Error(getErrorMessage(e, 'getPermissions error'))
  }
}

const grantPermissions = async ({ role, permissions }: { role: string; permissions: string[] }) => {
  return await grantPermissionsOnTopic({ ...apiPerTopicParams(), role, permissions })
}

const revokePermissions = async ({ role }: { role: string }) => {
  return await revokePermissionsOnTopic({ ...apiPerTopicParams(), role })
}

const setBacklogQuota = async ({
  backlogQuotaType,
  backlogQuota
}: {
  backlogQuotaType: 'destination_storage'
  backlogQuota: BacklogQuotaImpl
}) => {
  return await setBacklogQuotaOnTopic({
    ...apiPerTopicParams(),
    backlogQuotaType,
    backlogQuota
  })
}

const setDispatchRate = async ({ dispatchRate }: { dispatchRate: DispatchRateImpl }) => {
  return await setDispatchRateOnTopic({
    ...apiPerTopicParams(),
    dispatchRate
  })
}

const setMessageTTL = async ({ messageTTL }: { messageTTL: number }) => {
  return await setMessageTTLOnTopic({
    ...apiPerTopicParams(),
    messageTTL
  })
}

const setRetention = async ({ retentionPolicies }: { retentionPolicies: RetentionPolicies }) => {
  return await setRetentionOnTopic({
    ...apiPerTopicParams(),
    retentionPolicies
  })
}

const setCompactionThreshold = async ({ compactionThreshold }: { compactionThreshold: number }) => {
  return await setCompactionThresholdOnTopic({
    ...apiPerTopicParams(),
    compactionThreshold
  })
}

const setSubscriptionDispatchRate = async ({
  subscriptionDispatchRate
}: {
  subscriptionDispatchRate: DispatchRateImpl
}) => {
  return await setSubscriptionDispatchRateOnTopic({
    ...apiPerTopicParams(),
    subscriptionDispatchRate
  })
}

const setSubscribeRate = async ({ subscribeRate }: { subscribeRate: SubscribeRate }) => {
  return await setSubscribeRateOnTopic({
    ...apiPerTopicParams(),
    subscribeRate
  })
}

const setMaxProducers = async ({ maxProducers }: { maxProducers: number }) => {
  return await setMaxProducersOnTopic({
    ...apiPerTopicParams(),
    maxProducers
  })
}

const setMaxConsumers = async ({ maxConsumers }: { maxConsumers: number }) => {
  return await setMaxConsumersOnTopic({
    ...apiPerTopicParams(),
    maxConsumers
  })
}

const setMaxConsumersPerSubscription = async ({
  maxConsumersPerSubscription
}: {
  maxConsumersPerSubscription: number
}) => {
  return await setMaxConsumersPerSubscriptionOnTopic({
    ...apiPerTopicParams(),
    maxConsumersPerSubscription
  })
}

const setReplicationClusters = async ({
  replicationClusters
}: {
  replicationClusters: string[]
}) => {
  return await setReplicationClustersOnTopic({
    ...apiPerTopicParams(),
    clusters: replicationClusters
  })
}

const viewMessages = async (shortTopicName: string, subscriptionName: string, count: number) => {
  if (count <= 0) throw t('topic.subscription.messageGreaterThanZero')
  if (count > 100) throw t('schema.maxMessageNotification')
  if (!subscriptionName) throw t('topic.selectSubMessage')

  // Below code is extraction of logics at pulsar admin client
  // https://github.com/streamnative/pulsar/blob/master/pulsar-client-admin/src/main/java/org/apache/pulsar/client/admin/internal/TopicsImpl.java#L880-L913
  messages.value = []
  try {
    const params = apiPerTopicParams()
    params.topicName = shortTopicName || params.topicName
    for (let i = 1; i <= count; i++) {
      const res = await peekNthMessage({
        ...params,
        subscriptionName: encodeURIComponent(subscriptionName),
        n: i
      })

      let properties: Record<string, string | string[]> = {}
      Object.entries(res.headers).forEach(([key, value]) => {
        if (key.startsWith('x-pulsar-property-')) {
          properties[key.slice('x-pulsar-property-'.length)] = value
        } else if (key === 'x-pulsar-property') {
          // x-pulsar-property: {"id":"cca5be82-2107-11f0-add2-acde48001122","source-system":"in-memory","app-name":"key-shared-publisher"}
          properties = { ...properties, ...JSON.parse(value) }
        } else if (key === 'x-pulsar-batch-size' || key === 'x-pulsar-num-batch-message') {
          properties[key] = value
        }
      })
      const messageId = res.headers['x-pulsar-message-id']
      const tokenizedMessageId = messageId.split(':')
      const payload = new Uint8Array(res.data as unknown as ArrayBuffer)
      // returned binary is java serialized object.
      // there is no good library at npm to deserialize java object without defining java object schema.
      // so simply find the the separator for the payload and extract value is the simplest way.
      const data = payload.slice(payload.indexOf(2) + 1)
      const dataString = new TextDecoder().decode(data)

      messages.value = [
        ...messages.value,
        {
          properties: JSON.stringify(properties, null, 2),
          messageId: res.headers['x-pulsar-message-id'],
          message: dataString,
          ledgerId: tokenizedMessageId[0],
          entryId: tokenizedMessageId[1],
          data: dataString
        }
      ]
    }
  } catch (e) {
    // peekNthMessage() will respond with 404 when n is bigger than message it has.
    // in such case, simply catch and ignore as no more messages to read.
    if (!axios.isAxiosError(e) || e.response?.status !== 404) {
      throw e
    }
  }
}

const activeTopicDetail = computed(() => {
  const { topic } = usePulsarState()
  return topic.value ? topicMap.value[topic.value] : undefined
})

export const init = (initialState: PulsarState) => {
  const { organization, instance, clusterUid, tenant, namespace } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()
  const { canDescribeTopicList } = rbacManager()
  const { isActiveClusterAvailable } = useCluster()
  const valueChanged = async ([org, instance, clusterUid, ten, ns, ab, isActiveClusterAvailable]: [
    string | undefined,
    string | undefined,
    string | undefined,
    string | undefined,
    string | undefined,
    boolean | undefined,
    boolean | undefined
  ]) => {
    if (ab) {
      return
    }

    if (!org || !instance || !clusterUid || !ten || !ns) {
      resetTopicState()
      lastSeen = undefined
      return
    }
    const seen = `${org}/${instance}/${clusterUid}/${ten}/${ns}`
    if (lastSeen !== seen && canDescribeTopicList() && isActiveClusterAvailable) {
      // reset topic state when tenant or namespace is changed
      resetTopicState()
      await getTopics({
        organization: org,
        clusterUid,
        tenant: ten,
        namespace: ns
      })
      lastSeen = seen
    }
  }

  watch(
    [
      organization,
      instance,
      clusterUid,
      tenant,
      namespace,
      isRbacUpdating,
      isActiveClusterAvailable
    ],
    valueChanged
  )

  return valueChanged([
    initialState.organization,
    initialState.instance,
    initialState.clusterUid,
    initialState.tenant,
    initialState.namespace,
    isRbacUpdating.value,
    isActiveClusterAvailable.value
  ])
}

const topicCreatedSuccess = (topicDetail: TopicDetails) => {
  topicMap.value[topicDetail.topicName] = topicDetail
  fillPartitionedSubTopics(topicDetail, topicMap.value)
}

export const systemTopicNames = [
  '__change_events',
  '__transaction_buffer_snapshot',
  '__transaction_buffer_snapshot_segments',
  '__transaction_buffer_snapshot_indexes',
  '__transaction_pending_ack',
  '__pending_ack_state',
  '__transaction_log_',
  '_confluent-ksql-',
  '__kafka_connect_offset_storage'
]

export const systemSubscriptionNamePrefixes = ['__', 'compaction-worker']

const isEditableSystemTopic = (tenant?: string, namespace?: string, topicName?: string) => {
  return tenant === 'sn' && namespace === 'system' && topicName === 'audit_log_all'
}

const topicRootDetails = computed(() => {
  // only contains root topics.  i.e. [c1, c2]
  return Object.values(topicMap.value)
    .filter(detail => detail.partitionId === undefined)
    .sort((a, b) => a.topicName.localeCompare(b.topicName))
})

const allTopicsWithoutSystemTopics = computed(() => {
  return topicRootDetails.value.filter(details => !systemTopicNames.includes(details.topicName))
})

const topicStatsPartitions = computed(() => {
  const partitions = ((topicStats.value as PartitionedTopicStatsImpl)?.partitions ?? {}) as Record<
    string,
    PartitionedTopicStatsImpl
  >

  return Object.keys(partitions)
    .map(partitionKey => {
      const partition = partitions[partitionKey]
      const name = partitionKey.split('/')[4]
      const partitionId = name.split(PARTITION_SEPARATOR)[1]

      return {
        ...partition,
        name,
        partitionId: parseInt(partitionId),
        producerCounts: partition.publishers?.length ?? 0,
        subscriptionsCounts: Object.keys(partition?.subscriptions ?? {}).length
      }
    })
    .sort((a, b) => a.partitionId - b.partitionId)
})

export const useTopic = () => {
  const isSystemSubscription = (subscriptionName?: string) => {
    return systemSubscriptionNamePrefixes.some(prefix => subscriptionName?.startsWith(prefix))
  }

  const isSystemTopic = (tenant?: string, namespace?: string, topicName?: string) => {
    const { isSystemTenantNamespace } = useTenantNamespace()
    return (
      (tenant && namespace && isSystemTenantNamespace(tenant, namespace)) ||
      (topicName && systemTopicNames.includes(topicName))
    )
  }

  const isTopicEditable = (tenant?: string, namespace?: string, topicName?: string) => {
    return (
      isEditableSystemTopic(tenant, namespace, topicName) ||
      !isSystemTopic(tenant, namespace, topicName)
    )
  }

  return {
    topicMap,
    topicDetails: computed(() => {
      // contains all topics: i.e. [c1, c2, c2-partition-0, c2-partition-1]
      return Object.values(topicMap.value).sort((a, b) => a.topicName.localeCompare(b.topicName))
    }),
    topicRootDetails,
    allTopicsWithoutSystemTopics,
    // selectedTopicDetail,
    systemTopicNames,
    topicStats,
    bundleRange,
    topicBroker,
    schemas,
    topicInternalStats,
    partitionedTopicnternalStats,
    compactionStatus,
    messages,
    isTopicLoading,
    activeTopicDetail,
    getTopics,
    createTopic,
    updateTopic,
    deleteTopic,
    getTopicStats,
    getIndividualTopicStats,
    createSubscription,
    deleteSubscription,
    resetAllSubMessage,
    clearAllSubMessage,
    getBundleRange,
    getTopicBroker,
    getAllSchemas,
    deleteAllSchemas,
    createSchema,
    getTopicStatsInternal,
    unloadTopic,
    compactTopic,
    viewMessages,
    resetTopicState,
    getCompactionStatus,
    ensureTopicPartitionIsLoaded,
    topicFullNameToDetail,
    ensureTopicMetricsIsLoaded,
    getPermissions,
    grantPermissions,
    revokePermissions,
    init,
    errorGettingTopics,
    topicCreatedSuccess,
    activeTopicPolicies,
    getTopicPolicies,
    isSystemTopic,
    isSystemSubscription,
    isTopicEditable,
    topicStatsPartitions,
    setBacklogQuota,
    setDispatchRate,
    setMessageTTL,
    setRetention,
    setCompactionThreshold,
    setSubscriptionDispatchRate,
    setSubscribeRate,
    setMaxProducers,
    setMaxConsumers,
    setMaxConsumersPerSubscription,
    setReplicationClusters
  }
}
